import { useImperativeHandle, useRef, useState, useSyncExternalStore, type KeyboardEvent, type Ref } from 'react'
import { Skeleton } from '../../platform/async/Skeleton'
import { Panel } from '../../platform/primitives/Panel'
import { badgeText, cellLabel, dayStatus, monthGrid, moveDay, type Day, type MonthShape } from './calendarGrid'
import { monthName } from './listSections'
import type { YearMonth } from './monthParam'
import type { LocalDate } from './recurringRules'

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** A day cell's height, shared by the skeleton so the grid does not move when the month resolves (R9). */
export const DAY_CELL_HEIGHT = 58

export const monthKey = (ym: YearMonth) => `${ym.year}-${String(ym.month).padStart(2, '0')}`

/** `null` for a blank leading or trailing cell, else the day number. */
function weekRows(shape: MonthShape): (number | null)[][] {
  const rows: (number | null)[][] = []
  for (let w = 0; w < shape.weeks; w += 1) {
    const row: (number | null)[] = []
    for (let c = 0; c < 7; c += 1) {
      const n = w * 7 + c - shape.firstWeekday + 1
      row.push(n >= 1 && n <= shape.dayCount ? n : null)
    }
    rows.push(row)
  }
  return rows
}

/**
 * Weekday letters, named in full: "T" and "S" each stand for two days.
 * `aria-hidden` on the letter, so the name is not read twice.
 */
function WeekdayRow() {
  return (
    <div role="row" className="rec-cal__row">
      {WEEKDAYS.map((w) => (
        <div key={w} role="columnheader" aria-label={w} className="rec-cal__weekday">
          <span aria-hidden="true">{w.charAt(0)}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * The day's ONE status badge: the count of its bills, coloured and SHAPED by
 * the most urgent of them (rule 11) — a triangle on a filled pill for overdue,
 * a ring for due, a dash for skipped, a disc for paid. Hidden from assistive
 * tech: the day's name already carries every count. An inflow-only day has no
 * badge, because an inflow is not a bill (D6). The slot is always drawn, so
 * every cell is the same height whether or not it has a badge.
 */
function Badge({ data }: { data: Day | undefined }) {
  const status = data ? dayStatus(data) : null
  return (
    <span className="rec-cal__badge-slot" aria-hidden="true">
      {data && status !== null && (
        <span className={`rec-badge rec-badge--${status}`} data-badge={status}>
          <span className={`rec-badge__glyph rec-badge__glyph--${status}`} />
          {badgeText(data.counts.bills)}
        </span>
      )}
    </span>
  )
}

/**
 * Which grid day a hovered or focused list row belongs to. A tiny external
 * store rather than state on the board, so a pointer running down a forty-row
 * list re-renders the grid and never the list (shape §4).
 */
export type LinkStore = {
  get: () => number | null
  set: (day: number | null) => void
  subscribe: (onChange: () => void) => () => void
}

export function createLinkStore(): LinkStore {
  let value: number | null = null
  const subscribers = new Set<() => void>()
  return {
    get: () => value,
    set: (day) => {
      if (day === value) return
      value = day
      subscribers.forEach((f) => f())
    },
    subscribe: (onChange) => {
      subscribers.add(onChange)
      return () => {
        subscribers.delete(onChange)
      }
    },
  }
}

/** What the board may ask of the grid: move focus (and the roving tab stop) to a day. */
export type GridHandle = { focusDay: (day: number) => void }

const NO_LINK: LinkStore = { get: () => null, set: () => undefined, subscribe: () => () => undefined }

/**
 * The month grid (M7-R8): a CSS grid with `role="grid"`, never a table (rule 3).
 *
 * **The day button is the control, and the grid is one tab stop** (Ruling 16).
 * Each day's `gridcell` holds one `<button>` with a roving `tabIndex`; arrow
 * keys move it by day and by week through `moveDay`, keyed by day number so a
 * blank leading cell can never hold focus. Blank cells are `aria-hidden` and
 * focusable by nothing. Enter, Space and click activate a day that has entries
 * — or the selected day, so it can always be cleared; an empty day does
 * nothing. `onActivate`'s `pointer` is true for a pointer or touch click
 * (`detail > 0`) and false for the keyboard, which must never scroll.
 *
 * Mount it with `key` set to the month: the tab stop starts on the selected
 * day, else today, else the 1st, and must not carry over from another month.
 */
export function CalendarGrid({
  ym,
  days,
  today,
  selectedDay,
  links = NO_LINK,
  onActivate,
  handle,
  describedBy,
}: {
  ym: YearMonth
  days: Map<number, Day>
  today: LocalDate
  selectedDay: number | null
  links?: LinkStore
  onActivate: (day: number, pointer: boolean) => void
  handle?: Ref<GridHandle>
  /** The id of a caption that describes the grid, read once when it takes focus. */
  describedBy?: string
}) {
  const shape = monthGrid(ym)
  const todayDay = today.slice(0, 7) === monthKey(ym) ? Number(today.slice(8, 10)) : null
  // The roving tab stop.
  const [stopDay, setStopDay] = useState(selectedDay ?? todayDay ?? 1)
  const linkedDay = useSyncExternalStore(links.subscribe, links.get)
  const gridRef = useRef<HTMLDivElement>(null)

  function focusDay(n: number) {
    setStopDay(n)
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${n}"]`)?.focus()
  }
  useImperativeHandle(handle, () => ({ focusDay }))

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const at = Number((e.target as HTMLElement).dataset.day)
    if (!at) return
    const next = moveDay(e.key, at, shape)
    if (next === null) return
    e.preventDefault()
    focusDay(next)
  }

  return (
    <div ref={gridRef} role="grid" aria-label={`${monthName(ym)} ${ym.year}`} aria-describedby={describedBy} className="rec-cal__grid" onKeyDown={onKeyDown}>
      <WeekdayRow />
      {weekRows(shape).map((row, w) => (
        <div key={w} role="row" className="rec-cal__row">
          {row.map((n, c) => {
            if (n === null) return <div key={c} role="gridcell" aria-hidden="true" className="rec-cal__cell" />
            const data = days.get(n)
            const isToday = n === todayDay
            const isSelected = n === selectedDay
            return (
              <div key={c} role="gridcell" aria-selected={isSelected} className="rec-cal__cell">
                <button
                  type="button"
                  data-day={n}
                  tabIndex={n === stopDay ? 0 : -1}
                  aria-label={cellLabel(ym, n, data?.counts ?? { bills: 0, overdue: 0, upcoming: 0, skipped: 0, paid: 0, income: 0 }, isToday)}
                  aria-current={isToday ? 'date' : undefined}
                  className={[
                    'rec-cal__day',
                    'hoverable',
                    data ? 'rec-cal__day--busy' : '',
                    isToday ? 'rec-cal__day--today' : '',
                    isSelected ? 'rec-cal__day--selected' : '',
                    // The selected day's own edge already says "this one".
                    n === linkedDay && !isSelected ? 'rec-cal__day--linked' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onFocus={() => setStopDay(n)}
                  onClick={(e) => {
                    if (data || isSelected) onActivate(n, e.detail > 0)
                  }}
                >
                  <span className="rec-cal__num">{n}</span>
                  <Badge data={data} />
                </button>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

/**
 * Loading (R9): the month's real shape — weekday headers, then its actual
 * number of week rows — with one skeleton block per day and no numerals, so it
 * cannot be misread as a quiet month and does not jump from five rows to six
 * when the data lands. Never a spinner. The grid's Panel only: the board
 * around it is the caller's.
 */
export function CalendarSkeleton({ ym }: { ym: YearMonth }) {
  const shape = monthGrid(ym)
  return (
    <Panel className="rec-cal__panel rec-board__grid">
      <div role="grid" aria-busy="true" aria-label={`Loading ${monthName(ym)} ${ym.year}`} className="rec-cal__grid">
        <WeekdayRow />
        {weekRows(shape).map((row, w) => (
          <div key={w} role="row" className="rec-cal__row">
            {row.map((n, c) =>
              n === null ? (
                <div key={c} role="gridcell" aria-hidden="true" className="rec-cal__cell" />
              ) : (
                <div key={c} role="gridcell" className="rec-cal__cell rec-cal__cell--loading">
                  <Skeleton height={DAY_CELL_HEIGHT} />
                </div>
              ),
            )}
          </div>
        ))}
      </div>
    </Panel>
  )
}
